import React from 'react';
import { useSelector } from 'react-redux';
import { Star, Clock, BookOpen, Users, Award, Globe, DollarSign, Calendar } from 'lucide-react';

const CourseDetailsView = () => {
    const { courseList: course } = useSelector((state) => state.course);

    if (!course) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <p className="text-secondary fs-5">Loading course details...</p>
            </div>
        );
    }

    const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    function extractFileNameFromUrl(url) {
        if (typeof url !== "string") return "";
        return url.split("/").pop() || "";
    }

    const formatPrice = (price) => parseFloat(price).toFixed(2);

    return (
        <div className="card shadow-sm mb-4 mt-2">
            {/* Header */}
            <div className="card-header bg-primary text-white rounded-top p-4">
                <div className="row align-items-center">
                    {course.image && (
                        <div className="col-auto">
                            <img
                                src={course.image}
                                alt={course.name}
                                className="rounded"
                                style={{ width: '120px', height: 'auto', objectFit: 'cover' }}
                            />
                        </div>
                    )}
                    <div className="col">
                        <div className="d-flex gap-2 mb-2 flex-wrap">
                            <span className="badge bg-light text-primary">{course.course_category?.name}</span>
                        </div>
                        <h2 className="fw-bold mb-0 text-white">{course.name}</h2>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="card-body">
                <div className="row text-center mb-4">
                    <div className="col-md-3">
                        <DollarSign className="text-success mb-1" />
                        <div><small className="text-muted">Price</small></div>
                        <strong>₹{formatPrice(course.price)}</strong>
                    </div>
                    <div className="col-md-3">
                        <Globe className="text-primary mb-1" />
                        <div><small className="text-muted">Language</small></div>
                        <strong>{course.language}</strong>
                    </div>
                    <div className="col-md-3">
                        <Users className="text-info mb-1" />
                        <div><small className="text-muted">Purchases</small></div>
                        <strong>{course.total_purchage}</strong>
                    </div>
                    <div className="col-md-3">
                        <Star className="text-warning mb-1" />
                        <div><small className="text-muted">Rating</small></div>
                        <strong>{parseFloat(course.avg_review).toFixed(1)} ({course.total_review})</strong>
                    </div>
                </div>

                {/* Details Grid */}
                <div className="row">
                    <div className="col-lg-8">
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">📘 Course Details</h5>
                        <div className="row mb-3">
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <BookOpen className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Lessons</small><br />
                                    <strong>{course.total_lession}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Clock className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Duration</small><br />
                                    <strong>{course.duration}</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Calendar className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Validity</small><br />
                                    <strong>{course.validity} days</strong>
                                </div>
                            </div>
                            <div className="col-sm-6 mb-2 d-flex align-items-start">
                                <Calendar className="me-2 text-primary" />
                                <div>
                                    <small className="text-muted">Created On</small><br />
                                    <strong>{formatDate(course.createdAt)}</strong>
                                </div>
                            </div>
                        </div>

                        <h6 className="fw-semibold">Description</h6>
                        <p className="text-muted">{course.description}</p>

                        <h6 className='fw-semibold'>File</h6>
                        {
                            course?.file
                                ? (<a href={course?.file} target="_blank">{extractFileNameFromUrl(course?.file)}</a>)
                                : (<span>NA</span>)
                        }
                    </div>

                    {/* Trainer */}
                    <div className="col-lg-4">
                        <h5 className="fw-semibold border-bottom pb-2 mb-3">👨‍🏫 Trainer</h5>
                        {course.assign_trainer?.length > 0 ? course.assign_trainer.map(({ trainer_details: trainer }) => (
                            <div key={trainer.id} className="card mb-3 border-0 shadow-sm">
                                <div className="card-body d-flex gap-3 align-items-start">
                                    {trainer.image ? (
                                        <img src={trainer.image} alt={trainer.full_name} className="rounded-circle" width="60" height="60" />
                                    ) : (
                                        <div className="rounded-circle bg-light text-center d-flex justify-content-center align-items-center" style={{ width: '60px', height: '60px' }}>
                                            <span className="fw-bold text-primary">{trainer.full_name.charAt(0)}</span>
                                        </div>
                                    )}
                                    <div>
                                        <h6 className="mb-1 fw-bold">{trainer.full_name}</h6>
                                        <p className="text-muted small mb-1">{trainer.email}</p>
                                        <div className="d-flex align-items-center small text-muted">
                                            <Star className="me-1 text-warning" size={14} />
                                            {parseFloat(trainer.avg_review).toFixed(1)} ({trainer.total_review})
                                        </div>

                                        {/* Certifications */}
                                        {trainer.trainer_certification?.length > 0 && (
                                            <>
                                                <small className="fw-semibold d-block mt-2"><Award size={14} className="me-1" /> Certifications</small>
                                                <ul className="small ps-3">
                                                    {trainer.trainer_certification.map(cert => (
                                                        <li key={cert.id}>{cert.name} - {cert.institution}</li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}

                                        {/* Education */}
                                        {trainer.trainer_education?.length > 0 && (
                                            <>
                                                <small className="fw-semibold d-block mt-2">🎓 Education</small>
                                                <ul className="small ps-3">
                                                    {trainer.trainer_education.map(edu => (
                                                        <li key={edu.id}>{edu.name} - {edu.university} ({edu.marks_in_percentage}%)</li>
                                                    ))}
                                                </ul>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <p className="text-muted">No trainer assigned.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseDetailsView;
